import { useState } from "react";
import { apiUrl } from "../utils/api";
import { createWorker } from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import "../style/Quiz.css";
import { parseQuizText } from "../utils/quizTextParser";
import { exportQuizText, importQuizText } from "../utils/quizTextExport";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
    import.meta.url
).toString();

const MAX_PDF_BYTES = 20 * 1024 * 1024;
const MAX_PDF_PAGES = 50;
const MAX_TEXT_BYTES = 2 * 1024 * 1024;

const CREATE_QUIZ_API_URL = process.env.REACT_APP_CREATE_QUIZ_API_URL
    || apiUrl("create_quiz.php");
const UPDATE_QUIZ_API_URL = process.env.REACT_APP_UPDATE_QUIZ_API_URL
    || apiUrl("update_quiz.php");

const newQuestion = () => ({
    question: "",
    options: ["", "", "", ""],
    correctIndex: 0
});

function questionForForm(question) {
    const correctIndex = question.Options.indexOf(question.CorrectAnswer);
    return {
        question: question.Question,
        options: question.Options,
        correctIndex: correctIndex < 0 ? 0 : correctIndex
    };
}

export default function CreateQuizPage({
    text,
    schoolClassrooms,
    initialQuiz,
    onBack,
    onCreated
}) {
    const [title, setTitle] = useState(initialQuiz ? initialQuiz.title : "");
    const [schoolTrack, setSchoolTrack] = useState(
        initialQuiz ? initialQuiz.schoolTrack : ""
    );
    const [category, setCategory] = useState(initialQuiz ? initialQuiz.category : "");
    const [description, setDescription] = useState(initialQuiz ? initialQuiz.description : "");
    const [difficulty, setDifficulty] = useState(
        initialQuiz ? initialQuiz.difficulty : "easy"
    );
    const [status, setStatus] = useState(
        initialQuiz ? (initialQuiz.status || "published") : "published"
    );
    const [questions, setQuestions] = useState(
        initialQuiz ? initialQuiz.questions.map(questionForForm) : [newQuestion()]
    );
    const [duplicateQuestionIndexes, setDuplicateQuestionIndexes] = useState([]);
    const [error, setError] = useState("");
    const [pdfImportStatus, setPdfImportStatus] = useState("");
    const [textImportStatus, setTextImportStatus] = useState("");
    const [pdfFileName, setPdfFileName] = useState("");
    const [textFileName, setTextFileName] = useState("");
    const [isImportingPdf, setIsImportingPdf] = useState(false);
    const [isImportingText, setIsImportingText] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selectedClassroom = schoolClassrooms.find(
        (classroom) => classroom.name === schoolTrack
    );
    const availableSubjects = selectedClassroom ? selectedClassroom.subjects : [];
    const categoryForClassroom = (classroomName, preferredCategory = "") => {
        const classroom = schoolClassrooms.find((item) => item.name === classroomName);
        if (!classroom) {
            return "";
        }
        return classroom.subjects.find((subject) => subject.name === preferredCategory)?.name
            || classroom.subjects[0]?.name
            || "";
    };

    const updateQuestion = (questionIndex, update) => {
        setQuestions((currentQuestions) => currentQuestions.map((question, index) => (
            index === questionIndex ? { ...question, ...update } : question
        )));
    };

    const updateOption = (questionIndex, optionIndex, value) => {
        setDuplicateQuestionIndexes([]);
        setQuestions((currentQuestions) => currentQuestions.map((question, index) => {
            if (index !== questionIndex) {
                return question;
            }
            return {
                ...question,
                options: question.options.map((option, currentOptionIndex) => (
                    currentOptionIndex === optionIndex ? value : option
                ))
            };
        }));
    };

    const addOption = (questionIndex) => {
        setQuestions((currentQuestions) => currentQuestions.map((question, index) => (
            index === questionIndex
                ? { ...question, options: [...question.options, ""] }
                : question
        )));
    };

    const removeOption = (questionIndex, optionIndex) => {
        setQuestions((currentQuestions) => currentQuestions.map((question, index) => {
            if (index !== questionIndex) {
                return question;
            }
            const options = question.options.filter((_, currentIndex) => currentIndex !== optionIndex);
            let correctIndex = question.correctIndex;
            if (correctIndex === optionIndex) {
                correctIndex = 0;
            } else if (correctIndex > optionIndex) {
                correctIndex -= 1;
            }
            return { ...question, options, correctIndex };
        }));
    };

    const handlePdfImport = async (event) => {
        const file = event.target.files[0];
        event.target.value = "";
        if (!file) {
            return;
        }

        setError("");
        setPdfImportStatus("");
        if (!file.name.toLowerCase().endsWith(".pdf") || file.size > MAX_PDF_BYTES) {
            setError(file.size > MAX_PDF_BYTES
                ? text.pdfImportTooLarge
                : text.pdfImportInvalidFile);
            return;
        }

        setIsImportingPdf(true);
        setPdfImportStatus(text.pdfImportReading);
        let worker = null;
        let pdf = null;

        try {
            const loadingTask = pdfjsLib.getDocument({
                data: await file.arrayBuffer()
            });
            pdf = await loadingTask.promise;
            if (pdf.numPages > MAX_PDF_PAGES) {
                setError(text.pdfImportTooManyPages);
                return;
            }

            const pageTexts = [];
            for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
                const page = await pdf.getPage(pageNumber);
                const content = await page.getTextContent();
                let pageText = content.items
                    .map((item) => (
                        typeof item.str === "string"
                            ? `${item.str}${item.hasEOL ? "\n" : " "}`
                            : ""
                    ))
                    .join("")
                    .trim();

                if (pageText.length < 20) {
                    if (!worker) {
                        setPdfImportStatus(text.pdfImportOcr.replace("{progress}", "0%"));
                        worker = await createWorker("eng+ara", 1, {
                            logger: (progress) => {
                                if (progress.status === "recognizing text") {
                                    setPdfImportStatus(text.pdfImportOcr.replace(
                                        "{progress}",
                                        `${Math.round(progress.progress * 100)}%`
                                    ));
                                }
                            }
                        });
                    }
                    const viewport = page.getViewport({ scale: 2 });
                    const canvas = document.createElement("canvas");
                    canvas.width = Math.ceil(viewport.width);
                    canvas.height = Math.ceil(viewport.height);
                    const context = canvas.getContext("2d");
                    if (!context) {
                        throw new Error("Could not create a canvas for PDF OCR.");
                    }
                    await page.render({ canvasContext: context, viewport }).promise;
                    pageText = (await worker.recognize(canvas)).data.text;
                    canvas.width = 0;
                    canvas.height = 0;
                }

                pageTexts.push(pageText);
            }

            const imported = parseQuizText(pageTexts.join("\n"));
            if (imported.questions.length === 0) {
                setError(text.pdfImportNoQuestions);
                return;
            }
            if (imported.questions.length > 30) {
                setError(text.pdfImportQuestionLimit);
                return;
            }
            if ((initialQuiz || questions.some((question) => (
                question.question.trim()
                || question.options.some((option) => option.trim())
            )))
                && !window.confirm(text.pdfImportReplaceConfirm)) {
                return;
            }

            setQuestions(imported.questions);
            if (!title.trim()) {
                setTitle(file.name.replace(/\.pdf$/i, "").slice(0, 150));
            }
            setPdfImportStatus(text.pdfImportSuccess
                .replace("{count}", String(imported.questions.length))
                .replace("{unanswered}", String(imported.unansweredCount)));
        } catch (importError) {
            console.error("Quiz PDF import failed:", importError);
            setError(text.pdfImportError);
        } finally {
            try {
                if (worker) {
                    await worker.terminate();
                }
            } catch (cleanupError) {
                console.error("Quiz PDF OCR worker cleanup failed:", cleanupError);
            }
            try {
                if (pdf) {
                    await pdf.destroy();
                }
            } catch (cleanupError) {
                console.error("Quiz PDF document cleanup failed:", cleanupError);
            }
            setIsImportingPdf(false);
        }
    };

    const handleTextImport = async (event) => {
        const file = event.target.files[0];
        event.target.value = "";
        if (!file) {
            return;
        }

        setError("");
        setTextImportStatus("");
        if (file.size > MAX_TEXT_BYTES) {
            setError(text.textImportTooLarge);
            return;
        }

        setIsImportingText(true);
        try {
            const imported = importQuizText(await file.text());
            if (imported.questions.length === 0) {
                setError(text.textImportInvalid);
                return;
            }
            if (imported.questions.length > 30) {
                setError(text.pdfImportQuestionLimit);
                return;
            }
            if ((initialQuiz || questions.some((question) => (
                question.question.trim()
                || question.options.some((option) => option.trim())
            )))
                && !window.confirm(text.pdfImportReplaceConfirm)) {
                return;
            }

            setQuestions(imported.questions);
            setDuplicateQuestionIndexes([]);
            if (Object.prototype.hasOwnProperty.call(imported.metadata, "title")) {
                setTitle(imported.metadata.title.slice(0, 150));
            } else if (!title.trim()) {
                setTitle(file.name.replace(/\.txt$/i, "").slice(0, 150));
            }
            const importedSchoolTrack = Object.prototype.hasOwnProperty.call(
                imported.metadata,
                "schoolTrack"
            ) ? imported.metadata.schoolTrack : schoolTrack;
            setSchoolTrack(importedSchoolTrack);
            setCategory(categoryForClassroom(
                importedSchoolTrack,
                imported.metadata.category || category
            ));
            if (Object.prototype.hasOwnProperty.call(imported.metadata, "description")) {
                setDescription(imported.metadata.description.slice(0, 500));
            }
            if (["easy", "hard"].includes(imported.metadata.difficulty)) {
                setDifficulty(imported.metadata.difficulty);
            }
            if (["draft", "published"].includes(imported.metadata.status)) {
                setStatus(imported.metadata.status);
            }
            setTextImportStatus(text.textImportSuccess
                .replace("{count}", String(imported.questions.length))
                .replace("{unanswered}", String(imported.unansweredCount)));
        } catch (importError) {
            console.error("Quiz text import failed:", importError);
            setError(text.textImportError);
        } finally {
            setIsImportingText(false);
        }
    };

    const handleTextExport = () => {
        const content = exportQuizText({
            title,
            schoolTrack,
            category,
            description,
            difficulty,
            status,
            questions
        });
        const file = new Blob([content], { type: "text/plain;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${(title.trim() || "quiz").replace(/[<>:"/\\|?*]/g, "_")}.txt`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
    };

    const submitQuiz = async (nextStatus) => {
        setError("");
        setDuplicateQuestionIndexes([]);
        if (nextStatus === "published") {
            const duplicateIndexes = questions.reduce((indexes, question, index) => {
                const options = question.options.map((option) => option.trim());
                if (new Set(options).size !== options.length) {
                    indexes.push(index);
                }
                return indexes;
            }, []);
            if (duplicateIndexes.length > 0) {
                setDuplicateQuestionIndexes(duplicateIndexes);
                return;
            }
        }
        if (questions.some((question) => (
            !Number.isInteger(question.correctIndex)
            || question.correctIndex < 0
            || question.correctIndex >= question.options.length
        ))) {
            setError(text.pdfImportChooseAnswers);
            return;
        }
        setIsSubmitting(true);

        try {
            const response = await fetch(initialQuiz
                ? UPDATE_QUIZ_API_URL
                : CREATE_QUIZ_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...(initialQuiz ? { id: initialQuiz.id } : {}),
                    title,
                    schoolTrack,
                    category,
                    description,
                    difficulty,
                    status: nextStatus,
                    questions
                })
            });
            const result = await response.json();
            if (!response.ok) {
                setError(text.createQuizErrors[result.error] || text.createQuizGenericError);
                return;
            }
            onCreated();
        } catch (requestError) {
            console.error("Quiz creation request failed:", requestError);
            setError(text.createQuizConnectionError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        await submitQuiz(status);
    };

    const handleSaveDraft = async () => {
        await submitQuiz("draft");
    };

    return (
        <main className="create-quiz-page">
            <section className="create-quiz-card">
                <button className="quiz-back-btn" type="button" onClick={onBack}>
                    {text.backToQuizzes}
                </button>
                <header className="create-quiz-header">
                    <span className="quiz-label">{text.quizAuthorLabel}</span>
                    <h1>{initialQuiz ? text.editQuizTitle : text.createQuizTitle}</h1>
                    <p>{text.createQuizDescription}</p>
                </header>

                <form className="create-quiz-form" onSubmit={handleSubmit}>
                    <div className="pdf-import-panel">
                        <label htmlFor="quiz-pdf">{text.pdfImportLabel}</label>
                        <p>{text.pdfImportDescription}</p>
                        <input
                            id="quiz-pdf"
                            className="quiz-file-input"
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={(event) => {
                                setPdfFileName(event.target.files[0]?.name || "");
                                handlePdfImport(event);
                            }}
                            disabled={isImportingPdf || isSubmitting}
                        />
                        <label
                            className={`quiz-file-picker ${isImportingPdf || isSubmitting ? "disabled" : ""}`}
                            htmlFor="quiz-pdf"
                        >
                            <span className="quiz-file-picker-button">{text.chooseFile}</span>
                            <span className="quiz-file-name">
                                {pdfFileName || text.noFileChosen}
                            </span>
                        </label>
                        {isImportingPdf && (
                            <p className="pdf-import-status" role="status" aria-live="polite">
                                {pdfImportStatus}
                            </p>
                        )}
                        {pdfImportStatus && !isImportingPdf && (
                            <p className="pdf-import-status" role="status" aria-live="polite">
                                {pdfImportStatus}
                            </p>
                        )}
                    </div>
                    <div className="pdf-import-panel quiz-text-tools">
                        <label htmlFor="quiz-text-import">{text.textImportLabel}</label>
                        <p>{text.textImportDescription}</p>
                        <input
                            id="quiz-text-import"
                            className="quiz-file-input"
                            type="file"
                            accept="text/plain,.txt"
                            onChange={(event) => {
                                setTextFileName(event.target.files[0]?.name || "");
                                handleTextImport(event);
                            }}
                            disabled={isImportingText || isSubmitting}
                        />
                        <label
                            className={`quiz-file-picker ${isImportingText || isSubmitting ? "disabled" : ""}`}
                            htmlFor="quiz-text-import"
                        >
                            <span className="quiz-file-picker-button">{text.chooseFile}</span>
                            <span className="quiz-file-name">
                                {textFileName || text.noFileChosen}
                            </span>
                        </label>
                        <div className="quiz-text-tools-actions">
                            <button
                                className="quiz-secondary-btn"
                                type="button"
                                onClick={handleTextExport}
                                disabled={isSubmitting}
                            >
                                {text.exportQuizText}
                            </button>
                        </div>
                        {isImportingText && (
                            <p className="pdf-import-status" role="status" aria-live="polite">
                                {text.textImportReading}
                            </p>
                        )}
                        {textImportStatus && !isImportingText && (
                            <p className="pdf-import-status" role="status" aria-live="polite">
                                {textImportStatus}
                            </p>
                        )}
                    </div>
                    <div className="auth-field">
                        <label htmlFor="quiz-title">{text.quizTitleLabel}</label>
                        <input
                            id="quiz-title"
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            maxLength={150}
                            required
                        />
                    </div>
                    <div className="auth-field">
                        <label htmlFor="quiz-school-track">{text.schoolClassroom}</label>
                        <select
                            id="quiz-school-track"
                            value={schoolTrack}
                            onChange={(event) => {
                                const nextSchoolTrack = event.target.value;
                                setSchoolTrack(nextSchoolTrack);
                                setCategory(categoryForClassroom(nextSchoolTrack));
                            }}
                            required
                        >
                            <option value="" disabled>{text.selectClassroom}</option>
                            {schoolClassrooms.map((classroom) => (
                                <option key={classroom.id} value={classroom.name}>
                                    {classroom.name === "General"
                                        ? text.generalClassroom
                                        : classroom.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="auth-field">
                        <label htmlFor="quiz-subject">{text.subjectLabel}</label>
                        <select
                            id="quiz-subject"
                            value={category}
                            onChange={(event) => setCategory(event.target.value)}
                            disabled={!schoolTrack}
                            required
                        >
                            <option value="" disabled>
                                {schoolTrack ? text.selectSubject : text.selectClassroomFirst}
                            </option>
                            {availableSubjects.map((subject) => (
                                <option key={subject.id} value={subject.name}>
                                    {subject.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="auth-field">
                        <label htmlFor="quiz-description">{text.quizDescriptionLabel}</label>
                        <textarea
                            id="quiz-description"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                            maxLength={500}
                            rows={3}
                        />
                    </div>
                    <div className="auth-field">
                        <label htmlFor="quiz-difficulty">{text.quizDifficulty}</label>
                        <select
                            id="quiz-difficulty"
                            value={difficulty}
                            onChange={(event) => setDifficulty(event.target.value)}
                            required
                        >
                            <option value="easy">{text.easyDifficulty}</option>
                            <option value="hard">{text.hardDifficulty}</option>
                        </select>
                    </div>
                    <div className="auth-field">
                        <label>{text.quizStatusLabel}</label>
                        <div className="quiz-status-toggle">
                            <label>
                                <input
                                    type="radio"
                                    name="quiz-status"
                                    checked={status === "published"}
                                    onChange={() => setStatus("published")}
                                />
                                {text.publishedQuiz}
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="quiz-status"
                                    checked={status === "draft"}
                                    onChange={() => setStatus("draft")}
                                />
                                {text.draftQuiz}
                            </label>
                        </div>
                    </div>

                    <div className="create-quiz-questions">
                        <h2>{text.questions}</h2>
                        {questions.map((question, questionIndex) => (
                            <fieldset className="create-question" key={questionIndex}>
                                <legend>
                                    {text.question} {questionIndex + 1}
                                </legend>
                                {questions.length > 1 && (
                                    <button
                                        className="remove-question-btn"
                                        type="button"
                                        onClick={() => setQuestions((currentQuestions) =>
                                            currentQuestions.filter((_, index) => index !== questionIndex)
                                        )}
                                    >
                                        {text.removeQuestion}
                                    </button>
                                )}
                                <div className="auth-field">
                                    <label htmlFor={`question-${questionIndex}`}>
                                        {text.questionTextLabel}
                                    </label>
                                    <input
                                        id={`question-${questionIndex}`}
                                        value={question.question}
                                        onChange={(event) => updateQuestion(questionIndex, {
                                            question: event.target.value
                                        })}
                                        maxLength={1000}
                                        required
                                    />
                                </div>

                                <div className="create-options">
                                    <h3>{text.answerOptionsLabel}</h3>
                                    {duplicateQuestionIndexes.includes(questionIndex) && (
                                        <p className="auth-error" role="alert">
                                            {text.duplicateOptionsInQuestion.replace(
                                                "{question}",
                                                String(questionIndex + 1)
                                            )}
                                        </p>
                                    )}
                                    {question.options.map((option, optionIndex) => (
                                        <div className="create-option-row" key={optionIndex}>
                                            <label htmlFor={`option-${questionIndex}-${optionIndex}`}>
                                                {text.optionLabel} {optionIndex + 1}
                                            </label>
                                            <input
                                                id={`option-${questionIndex}-${optionIndex}`}
                                                value={option}
                                                onChange={(event) => updateOption(
                                                    questionIndex,
                                                    optionIndex,
                                                    event.target.value
                                                )}
                                                maxLength={500}
                                                required
                                            />
                                            <label className="correct-option-label">
                                                <input
                                                    type="radio"
                                                    name={`correct-${questionIndex}`}
                                                    checked={question.correctIndex === optionIndex}
                                                    onChange={() => updateQuestion(questionIndex, {
                                                        correctIndex: optionIndex
                                                    })}
                                                />
                                                {text.correctAnswerLabel}
                                            </label>
                                            {question.options.length > 2 && (
                                                <button
                                                    className="remove-option-btn"
                                                    type="button"
                                                    onClick={() => removeOption(questionIndex, optionIndex)}
                                                >
                                                    {text.removeOption}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {question.options.length < 6 && (
                                        <button
                                            className="quiz-secondary-btn"
                                            type="button"
                                            onClick={() => addOption(questionIndex)}
                                        >
                                            {text.addOption}
                                        </button>
                                    )}
                                </div>
                            </fieldset>
                        ))}
                        {questions.length < 30 && (
                            <button
                                className="quiz-secondary-btn"
                                type="button"
                                onClick={() => setQuestions((currentQuestions) => [
                                    ...currentQuestions,
                                    newQuestion()
                                ])}
                            >
                                {text.addQuestion}
                            </button>
                        )}
                    </div>

                    {error && <p className="auth-error" role="alert">{error}</p>}
                    <div className="create-quiz-actions">
                        <button
                            className="quiz-secondary-btn"
                            type="button"
                            onClick={handleSaveDraft}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? text.authSubmitting : text.saveAsDraft}
                        </button>
                        <button
                            className="quiz-btn"
                            type="submit"
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? text.authSubmitting
                                : initialQuiz ? text.saveChanges : text.saveQuiz}
                        </button>
                    </div>
                </form>
            </section>
        </main>
    );
}
